/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { StyledContainer } from '../../Content.styles';
import {
  StyledSubheader,
  StyledHeadline,
  StyledParagraph,
} from './MalpracticeReportsSummary/MalpracticeReportsSummary.styles';
import { RootState } from '../../../../store';
import * as thunks from '../../../../store/malpracticeReports/thunks';
import Loader from '../../../../components/Loader/Loader';
import * as actions from '../../../../store/malpracticeReports/actions';
import { ProcessingError, SearchQuery } from '../../../../types/Plagiarism';
import {
  PlagiarismError,
  SectionBusinessId,
  PlagiarismCheckState,
  SortOrder,
  Roles,
} from '@cambridgeassessment/lib-ee-commons';
import { formatDate } from '../../../../util/dateFormatter';
import ReportTable, { SortingParams, useSorting } from './ReportTable/ReportTable';
import { Authorized } from 'lib-browser';

interface Props {
  match: {
    params: {
      seriesId: SectionBusinessId['seriesId'];
      assessmentCode: SectionBusinessId['assessmentCode'];
      componentNumber: SectionBusinessId['componentNumber'];
      sectionId: SectionBusinessId['sectionId'];
    };
  };
  queryParams: {
    searchQuery: Omit<SearchQuery, 'sectionId'>;
  };
  fetchComponentDetails: (x: Omit<SectionBusinessId, 'centreId' | 'learnerNumber' | 'learnerUniqueId'>) => void;
}

const PROCESSING_ERROR_MAP: { [rawError in PlagiarismError]: string } = {
  [PlagiarismError.UNSUPPORTED_FILETYPE]: 'Unsupported filetype',
  [PlagiarismError.PROCESSING_ERROR]: 'Processing error',
  [PlagiarismError.CANNOT_EXTRACT_TEXT]: 'Cannot extract text',
  [PlagiarismError.TOO_LITTLE_TEXT]: 'Too little text',
  [PlagiarismError.TOO_MUCH_TEXT]: 'Too much text',
  [PlagiarismError.TOO_MANY_PAGES]: 'Too many pages',
  [PlagiarismError.FILE_LOCKED]: 'File locked',
  [PlagiarismError.CORRUPT_FILE]: 'Corrupt file',
  [PlagiarismError.UNKNOWN]: 'Unknown error',
};

const PROCESSING_STATE_MAP: { [rawState in PlagiarismCheckState.FAILED_RETRY]: string } = {
  [PlagiarismCheckState.FAILED_RETRY]: 'Failed but re-trying',
};

const FailedReports: React.FC<Props> = ({
  match: {
    params: { seriesId, assessmentCode, componentNumber, sectionId },
  },
  queryParams,
  fetchComponentDetails,
}) => {
  const dispatch = useDispatch();
  const {
    malpracticeReport: { details },
    userData: { selectedRole },
  } = useSelector((state: RootState) => state);

  const reportParams = details.reportDetailsParams;
  const processingErrorsList = details.processingErrors;

  const loadMoreItems = (sortingParams?: SortingParams<ProcessingError>): void => {
    dispatch(
      thunks.getErrorReports(
        { seriesId, assessmentCode, componentNumber, sectionId },
        queryParams.searchQuery,
        sortingParams,
      ),
    );
  };

  useEffect(() => {
    loadMoreItems();
    fetchComponentDetails({ seriesId, assessmentCode, componentNumber, sectionId });
    return (): void => {
      dispatch(actions.resetState());
    };
  }, []);

  const { sort: onSort } = useSorting<ProcessingError>(
    (sortingParams) => {
      dispatch(actions.resetState());
      loadMoreItems(sortingParams);
    },
    {
      fieldName: 'centreId',
      order: SortOrder.ASC,
    },
  );

  const handleStatusMessage = (row: ProcessingError): string => {
    if (row.plagiarismReportState === PlagiarismCheckState.FAILED_RETRY) {
      return PROCESSING_STATE_MAP[row.plagiarismReportState];
    }

    return PROCESSING_ERROR_MAP[row.error] || PROCESSING_ERROR_MAP[PlagiarismError.UNKNOWN];
  };

  if (!processingErrorsList) return <Loader />;

  return (
    <Authorized
      selectedRole={selectedRole}
      roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER]}
    >
      {reportParams && (
        <StyledContainer data-qa-id='report-params'>
          <StyledSubheader>{reportParams.seriesName}</StyledSubheader>
          <StyledHeadline>
            {reportParams.assessmentCode} &ndash; {reportParams.componentNumber} - {reportParams.componentName} -{' '}
            {reportParams.sectionName}
          </StyledHeadline>
          <StyledParagraph>File processing errors in Turnitin</StyledParagraph>
        </StyledContainer>
      )}
      <ReportTable
        data={processingErrorsList}
        columns={[
          {
            label: 'Centre number',
            name: 'centreId',
            centre: true,
            sortable: true,
          },
          {
            label: 'Candidate name',
            name: 'learner',
            centre: false,
            sortable: true,
          },
          {
            label: 'Candidate number',
            name: 'learnerNumber',
            centre: true,
            sortable: true,
          },
          {
            label: 'Submission date',
            name: 'createdTime',
            centre: true,
            sortable: true,
            renderItem: (row) => formatDate(row.createdTime),
          },
          {
            label: 'Turnitin status',
            centre: true,
            sortable: false,
            renderItem: handleStatusMessage,
          },
        ]}
        loadMoreItems={loadMoreItems}
        onSort={onSort}
      />
    </Authorized>
  );
};

export default FailedReports;
